import { Box, Chip, FormControl, MenuItem, Typography } from "@mui/material";
import { NewCampaignContext } from "contexts/NewCampaign";
import { useContext, useState } from "react";
import { CustomEvent, Map } from "schemas/functions";

import StyledSelect from "components/Styled/Select";
import StyledTextField from "components/Styled/TextField";
import TimezoneAutocomplete from "components/TimezoneAutocomplete";
import { labelSx } from "features/Influencer/Autopilot/styles";
import { addBusinessDays } from "utils/time";

import StepNav from "../../StepNav";
import styles from "../../styles";

const EMAILS_PER_DAY_OPTIONS = [5, 10, 20, 25, 30];
const DEFAULT_EMAILS_PER_DAY = 20;
const DEFAULT_TIMEZONE = "America/New_York";

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => void;
  moveBack: () => void;
  submitLoading: boolean;
}

export default function Details({
  handleUpdateCampaign,
  moveBack,
  submitLoading,
}: Props) {
  const { campaign } = useContext(NewCampaignContext);

  const minDate = addBusinessDays(1).toISOString().split("T")[0];

  const [name, setName] = useState(campaign?.name ?? "");
  const [startDate, setStartDate] = useState(campaign?.startDate ?? minDate);
  const [emailsPerDay, setEmailsPerDay] = useState(
    campaign?.emailsPerDay ?? DEFAULT_EMAILS_PER_DAY,
  );
  const [timezone, setTimezone] = useState(
    campaign?.timezone ?? DEFAULT_TIMEZONE,
  );

  const canContinue = Boolean(name.trim() && startDate);

  const handleNext = () => {
    if (!canContinue) return;
    handleUpdateCampaign({
      name: name.trim(),
      startDate,
      emailsPerDay,
      timezone,
    });
  };

  return (
    <Box sx={styles.formColumn}>
      <Box sx={styles.card}>
        <Typography sx={styles.cardTitle}>Campaign details</Typography>
        <Typography
          sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, mb: 3 }}
        >
          Name your campaign and set when and how fast it sends.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: 0.75 }}>Campaign name</Typography>
            <StyledTextField
              fullWidth
              required
              value={name}
              onChange={(e: CustomEvent) => setName(e.target.value)}
              placeholder="e.g. Summer home decor, holiday gift guide"
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.75 }}>Start date</Typography>
              <StyledTextField
                type="date"
                fullWidth
                required
                value={startDate}
                onChange={(e: CustomEvent) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate }}
              />
            </Box>
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.75 }}>
                Emails per day
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={emailsPerDay}
                  onChange={(e: CustomEvent) =>
                    setEmailsPerDay(e.target.value as number)
                  }
                >
                  {EMAILS_PER_DAY_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} / day
                      {n === DEFAULT_EMAILS_PER_DAY && (
                        <Chip
                          label="Recommended"
                          size="small"
                          sx={{
                            ml: 1,
                            fontSize: 11,
                            fontWeight: 600,
                            height: 20,
                            bgcolor: "purple.light",
                            color: "purple.dark",
                          }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.75 }}>Timezone</Typography>
              <FormControl fullWidth>
                <TimezoneAutocomplete
                  value={timezone}
                  onChange={(_, newValue) =>
                    setTimezone(newValue || DEFAULT_TIMEZONE)
                  }
                />
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Box>

      <StepNav
        onBack={moveBack}
        onNext={handleNext}
        nextDisabled={!canContinue}
        nextLoading={submitLoading}
      />
    </Box>
  );
}
